import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Modal,
  Pressable,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";

import { RemoveScrollBar } from "react-remove-scroll-bar";

import { MediaSource } from "./ImageViewing/@types";
import Styles from "./imgStyles.module.css";
import ImageDefaultHeader from "./ImageViewing/components/ImageDefaultHeader";
import { AHIcon, Box, Circle, Image, TouchableBox } from "arkh";
import { useLightbox, useLightboxControls } from "./LightboxProvider";
import { useDeleteAlert } from "./ImageViewing/useDeleteAlert";

export function Lightbox() {
  const { activeLightbox } = useLightbox();
  const { closeLightbox } = useLightboxControls();
  const isActive = !!activeLightbox;

  if (!isActive) {
    return null;
  }

  const initialIndex = activeLightbox.index;
  const imgs = activeLightbox.images;
  return (
    <>
      <RemoveScrollBar />
      <LightboxInner
        imgs={imgs}
        initialIndex={initialIndex}
        onClose={closeLightbox}
      />
    </>
  );
}

function LightboxInner(props: {
  imgs: (MediaSource & { id: string })[];
  initialIndex: number;
  onClose: () => void;
}) {
  const initialImages = props.imgs;
  const initialIndex = props.initialIndex;
  const onClose = props.onClose;
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const images = useMemo(() => {
    return initialImages.filter((image) => !deletedIds.includes(image.id));
  }, [initialImages, deletedIds]);

  const [index, setIndex] = useState<number>(initialIndex);

  const imageIndex = useMemo(() => {
    return index && images.length > index ? index : 0;
  }, [index, images]);

  const canGoLeft = index >= 1;
  const canGoRight = index < images.length - 1;
  const onPressLeft = useCallback(() => {
    if (canGoLeft) {
      setIndex(index - 1);
    }
  }, [index, canGoLeft]);
  const onPressRight = useCallback(() => {
    if (canGoRight) {
      setIndex(index + 1);
    }
  }, [index, canGoRight]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        onPressLeft();
      } else if (e.key === "ArrowRight") {
        onPressRight();
      }
    },
    [onPressLeft, onPressRight]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  const img = images[imageIndex];

  const imageRef = useRef<any>(null);

  useLayoutEffect(() => {
    const imgRef = imageRef?.current?.nativeViewRef?.current;
    if (imgRef) {
      imgRef.style.pointerEvents = "none";
      imgRef.style.backgroundColor = "blue";
    }
  }, [imageRef]);
  useEffect(() => {
    if (images.length === 0) {
      onClose();
    }
  }, [images, onClose]);

  const onDeleteAlert = useDeleteAlert(() => {
    if (img?.id) {
      setDeletedIds((prev) => [...prev, img.id]);
      img.onDelete?.();
    }
  });

  if (!img) {
    return null;
  }

  return (
    <Modal
      visible={true}
      transparent
      presentationStyle="overFullScreen"
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.imageCenterer}>
        <Pressable
          style={[StyleSheet.absoluteFill]}
          onPress={() => {
            onClose();
          }}
        >
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                backdropFilter: "blur(1.6px)",
                background: "rgba(0,0,0,0.1)",
              } as any,
            ]}
          />
        </Pressable>

        <Image
          {...img.image}
          accessibilityIgnoresInvertColors
          cachePolicy="memory"
          style={[
            {
              $$css: true,
              _: Styles.ImageNoPointerEvents,
            } as any,
            styles.image,
          ]}
        />

        <Box
          style={{
            position: "absolute",
            width: "100%",
            left: 0,
            right: 0,
            bottom: 0,

            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row",
          }}
        >
          <TouchableBox
            disabled={!canGoLeft}
            onPress={onPressLeft}
            spaceHorizontal="medium"
            spaceBottom="medium"
            style={{
              opacity: canGoLeft ? 1 : 0.5,
            }}
          >
            <Circle size="large" color="background">
              <AHIcon type="arrow-left" size="medium" />
            </Circle>
          </TouchableBox>

          <TouchableBox
            disabled={!canGoRight}
            onPress={onPressRight}
            spaceHorizontal="medium"
            spaceBottom="medium"
            style={{
              opacity: canGoRight ? 1 : 0.5,
            }}
          >
            <Circle size="large" color="background">
              <AHIcon type="arrow-right" size="medium" />
            </Circle>
          </TouchableBox>
        </Box>
      </View>

      <Box style={styles.closeBtn} space="large">
        <ImageDefaultHeader onRequestClose={onClose} />
      </Box>
      {img.onDelete ? (
        <TouchableBox
          onPress={onDeleteAlert}
          space="large"
          spaceBottom="medium"
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
          }}
        >
          <Circle size="large" color="negative">
            <AHIcon type="trash" color="lightText" />
          </Circle>
        </TouchableBox>
      ) : null}
    </Modal>
  );
}

const styles = StyleSheet.create({
  imageCenterer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    resizeMode: "contain",
  },
  aviCenterer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  avi: {
    // @ts-ignore web-only
    maxWidth: `calc(min(400px, 100vw))`,
    // @ts-ignore web-only
    maxHeight: `calc(min(400px, 100vh))`,
    padding: 16,
    boxSizing: "border-box",
  },

  closeBtn: {
    position: "absolute",
    top: 0,
    right: 0,
    flexDirection: "row",
  },
  btn: {
    position: "absolute",
    backgroundColor: "#00000077",
    justifyContent: "center",
    alignItems: "center",
  },
  btnTablet: {
    width: 50,
    height: 50,
    borderRadius: 25,
    left: 30,
    right: 30,
  },
  btnMobile: {
    width: 44,
    height: 44,
    borderRadius: 22,
    left: 20,
    right: 20,
  },
  leftBtn: {
    right: "auto",
    top: "50%",
  },
  rightBtn: {
    left: "auto",
    top: "50%",
  },
  footer: {
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  blurredBackground: {
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  } as ViewStyle,
});
